import { Share03Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import {
  useCreateGramSocialShare,
  useCreateGramWithStorage,
  useGetGramByUserAndEvent,
  useGetGramFileUrl,
  useGetGramSocialShareBySessionUser,
} from '@sunrise-ui/api-client';
import { SessionStatus } from '@sunrise-ui/api/events/api';
import {
  AuthFlowError,
  Button,
  Colors,
  Modal,
  SESSION_SHARE_URL,
  TextReg,
  Tooltip,
  menuContentStyle,
  useAuthStore,
  useSnackbar,
} from '@sunrise-ui/primitives';
import * as Clipboard from 'expo-clipboard';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect, useRef, useState } from 'react';
import type { ViewStyle } from 'react-native';
import {
  Animated,
  Linking,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import { Menu } from 'react-native-paper';

type Props = {
  canShare: boolean;
  sessionId: string;
  eventId: string;
  sessionStatus?: SessionStatus;
  style?: ViewStyle;
  userId?: string;
};

export function SessionShareButton({
  canShare,
  sessionId,
  eventId,
  sessionStatus,
  style,
  userId,
}: Props) {
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [showCopyLinkModal, setShowCopyLinkModal] = useState(false);
  const [shareableLinkForModal, setShareableLinkForModal] = useState('');
  const [isCreatingShareLink, setIsCreatingShareLink] = useState(false);
  const [regenerateGramCooldownEnd, setRegenerateGramCooldownEnd] = useState<
    number | null
  >(null);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const shineAnimation = useRef(new Animated.Value(-100)).current;
  const { currentUserId } = useAuthStore();
  const { showSnackbar } = useSnackbar();

  // Use provided userId or fallback to currentUserId
  const targetUserId = userId || currentUserId;

  // Only fetch grams data when user can actually share to avoid unnecessary server load
  const {
    data: gramByUserEvent,
    isLoading: isLoadingGramByUserEvent,
    refetch: refetchGramByUserEvent,
  } = useGetGramByUserAndEvent(targetUserId, eventId, {
    enabled: canShare && Boolean(targetUserId && eventId),
  });

  // Fetch the file URL for the specific gram
  const { data: gramFileUrl } = useGetGramFileUrl(gramByUserEvent?.id, {
    enabled: canShare && Boolean(gramByUserEvent?.id),
  });

  // Check for existing social share for this session and user
  const { data: existingSocialShare } = useGetGramSocialShareBySessionUser(
    sessionId,
    targetUserId,
    {
      enabled: canShare && Boolean(sessionId && targetUserId),
    },
  );

  // Hook for creating gram social shares
  const { mutateAsync: createSocialShare } = useCreateGramSocialShare();

  // Hook for regenerating grams
  const { mutateAsync: regenerateGram } = useCreateGramWithStorage();

  // Helper function to ensure share link exists (called when menu opens)
  const ensureShareLinkExists = async () => {
    if (existingSocialShare || !gramByUserEvent) {
      return; // Already have a share link or can't create one
    }

    try {
      setIsCreatingShareLink(true);

      // Use the specific gram for this user and event
      const { id: gramStorageId } = gramByUserEvent;

      // Create a social share for this session
      await createSocialShare({
        createGramSocialShareInput: {
          gram_storage_id: gramStorageId,
          event_id: eventId,
          session_id: sessionId,
        },
        userId: targetUserId!,
      });
    } catch (error) {
      console.error('Error creating share link:', error);
    } finally {
      setIsCreatingShareLink(false);
    }
  };

  // Helper function to get shareable URL from existing share
  const getShareableUrl = (): string => {
    if (!existingSocialShare) {
      throw new Error('No shareable link available');
    }

    return `${SESSION_SHARE_URL}/og/${existingSocialShare.slug}`;
  };

  // Check if regenerate gram is in cooldown
  const isRegenerateInCooldown =
    regenerateGramCooldownEnd && Date.now() < regenerateGramCooldownEnd;

  // Cleanup cooldown timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
    };
  }, []);

  // Shine animation effect
  useEffect(() => {
    const createShineAnimation = () => {
      return Animated.sequence([
        Animated.timing(shineAnimation, {
          toValue: 100,
          duration: 1200,
          useNativeDriver: true,
        }),
        Animated.timing(shineAnimation, {
          toValue: -100,
          duration: 0,
          useNativeDriver: true,
        }),
      ]);
    };

    const runShineLoop = () => {
      createShineAnimation().start(() => {
        // Wait 3 seconds before next shine
        setTimeout(runShineLoop, 3000);
      });
    };

    // Start the first shine after a short delay
    const initialTimeout = setTimeout(runShineLoop, 1000);

    return () => {
      clearTimeout(initialTimeout);
      shineAnimation.stopAnimation();
    };
  }, [shineAnimation]);

  // Handle opening the share menu
  const handleOpenMenu = () => {
    // Check if regenerate gram is in cooldown
    if (isRegenerateInCooldown) {
      const remainingSeconds = Math.ceil(
        (regenerateGramCooldownEnd! - Date.now()) / 1000,
      );
      showSnackbar({
        text: `Gram regenerating... Please wait ${remainingSeconds} more seconds`,
        type: 'info',
      });
      return;
    }

    setIsMenuVisible(true);
    // Generate share link in background if needed
    void ensureShareLinkExists();
  };

  // Prevent menu from closing during share link creation
  const shouldShowMenu = isMenuVisible || isCreatingShareLink;

  const handleShareToLinkedIn = async () => {
    if (!currentUserId) {
      showSnackbar({
        text: 'Please log in to share',
        type: 'error',
      });
      setIsMenuVisible(false);
      return;
    }

    if (!existingSocialShare) {
      showSnackbar({
        text: 'Share link is being generated, please try again in a moment',
        type: 'info',
      });
      return;
    }

    try {
      // Get the shareable URL using our helper function
      const shareableUrl = getShareableUrl();

      // LinkedIn share URL format with the actual session link
      const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareableUrl)}`;

      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.open(linkedInUrl, '_blank');
      } else if (Platform.OS !== 'web') {
        const canOpen = await Linking.canOpenURL(linkedInUrl);
        if (canOpen) {
          await Linking.openURL(linkedInUrl);
        }
      }

      showSnackbar({
        text: 'LinkedIn opened with share link',
        type: 'success',
      });
    } catch (error) {
      console.error('Error opening LinkedIn:', error);
      showSnackbar({
        text: 'Failed to open LinkedIn',
        type: 'error',
      });
    }

    setIsMenuVisible(false);
  };

  const handleCopyLink = async () => {
    if (!currentUserId) {
      showSnackbar({
        text: 'Please log in to copy share link',
        type: 'error',
      });
      setIsMenuVisible(false);
      return;
    }

    if (!existingSocialShare) {
      showSnackbar({
        text: 'Share link is being generated, please try again in a moment',
        type: 'info',
      });
      return;
    }

    try {
      // Get the shareable URL using our helper function
      const shareableUrl = getShareableUrl();

      // Try to copy to clipboard with a web-first strategy
      let clipboardSuccess = false;

      if (Platform.OS === 'web') {
        // Web: prefer the native Clipboard API
        if (
          typeof navigator !== 'undefined' &&
          navigator.clipboard &&
          typeof navigator.clipboard.writeText === 'function'
        ) {
          try {
            await navigator.clipboard.writeText(shareableUrl);
            clipboardSuccess = true;
          } catch (webClipboardError) {
            console.warn(
              'navigator.clipboard.writeText failed:',
              webClipboardError,
            );
          }
        }

        // Fallback: execCommand('copy') using a hidden textarea
        if (!clipboardSuccess && typeof document !== 'undefined') {
          let textarea: HTMLTextAreaElement | null = null;
          try {
            textarea = document.createElement('textarea');
            textarea.value = shareableUrl;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'fixed';
            textarea.style.top = '-1000px';
            textarea.style.left = '-1000px';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            textarea.setSelectionRange(0, shareableUrl.length);

            const successful = document.execCommand('copy');
            clipboardSuccess = successful;
          } catch (execError) {
            console.warn('document.execCommand copy failed:', execError);
          } finally {
            if (textarea && textarea.parentNode) {
              textarea.parentNode.removeChild(textarea);
            }
          }
        }
      } else {
        // Native platforms: use expo-clipboard
        try {
          await Clipboard.setStringAsync(shareableUrl);
          clipboardSuccess = true;
        } catch (clipboardError) {
          console.warn(
            'Clipboard.setStringAsync failed (native):',
            clipboardError,
          );
        }
      }

      if (clipboardSuccess) {
        showSnackbar({
          text: 'Share link copied to clipboard!',
          type: 'success',
        });
      } else {
        // If clipboard failed, show the URL in a modal for manual copying
        setShareableLinkForModal(shareableUrl);
        setShowCopyLinkModal(true);
      }
    } catch (error) {
      console.error('Error copying share link:', error);
      showSnackbar({
        text: 'Failed to copy share link',
        type: 'error',
      });
    }

    setIsMenuVisible(false);
  };

  const handleDownloadGram = async () => {
    try {
      if (!currentUserId) {
        showSnackbar({
          text: 'Please log in to download grams',
          type: 'error',
        });
        setIsMenuVisible(false);
        return;
      }

      if (isLoadingGramByUserEvent) {
        showSnackbar({
          text: 'Loading gram information...',
          type: 'info',
        });
        return;
      }

      if (!gramByUserEvent) {
        showSnackbar({
          text: 'No gram found for this event.',
          type: 'error',
        });
        setIsMenuVisible(false);
        return;
      }

      // Check if we have the file URL ready
      if (!gramFileUrl?.aws_file_url) {
        showSnackbar({
          text: 'Gram not available for download',
          type: 'error',
        });
        setIsMenuVisible(false);
        return;
      }

      // Simply open the S3 link in a new tab
      const { aws_file_url } = gramFileUrl;

      if (Platform.OS === 'web') {
        // For web: Open in new tab
        if (typeof window !== 'undefined') {
          window.open(aws_file_url, '_blank');
        }
      } else {
        // For mobile: Open in browser
        const supported = await Linking.canOpenURL(aws_file_url);
        if (supported) {
          await Linking.openURL(aws_file_url);
        } else {
          throw new Error('Cannot open gram URL');
        }
      }

      showSnackbar({
        text: 'Opening gram in new tab',
        type: 'success',
      });
    } catch (error) {
      console.error('Error preparing gram download:', error);
      showSnackbar({
        text: 'Failed to prepare gram download',
        type: 'error',
      });
      setIsMenuVisible(false);
    }
  };

  const handleRegenerateGram = async () => {
    if (!targetUserId || !eventId) {
      showSnackbar({
        text: 'Unable to regenerate gram - missing user or event information',
        type: 'error',
      });
      setIsMenuVisible(false);
      return;
    }

    try {
      setIsMenuVisible(false);

      // Set cooldown period (1 minute from now)
      const cooldownEnd = Date.now() + 60000; // 60 seconds
      setRegenerateGramCooldownEnd(cooldownEnd);

      // Set timer to clear cooldown after 1 minute
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
      cooldownTimerRef.current = setTimeout(() => {
        // At the end of cooldown, create a fresh social share (new slug)
        void (async () => {
          try {
            // Ensure we have the latest gram id after regeneration
            const { data: refreshedGram } = await refetchGramByUserEvent();
            const latestGramId = refreshedGram?.id ?? gramByUserEvent?.id;
            if (!latestGramId || !targetUserId) return;
            await createSocialShare({
              createGramSocialShareInput: {
                gram_storage_id: latestGramId,
                event_id: eventId,
                session_id: sessionId,
              },
              userId: targetUserId,
            });
          } catch (e) {
            console.error('Create social share after cooldown failed:', e);
          } finally {
            setRegenerateGramCooldownEnd(null);
          }
        })();
      }, 60000);

      // Show initial notification
      showSnackbar({
        text: 'Regenerating gram... Your new gram should be ready in about a minute',
        type: 'info',
      });

      // Determine Google Drive folder based on session status
      const driveFolderId =
        sessionStatus === SessionStatus.Scheduled ||
        sessionStatus === SessionStatus.Confirmed
          ? '15Nlb7kHYFRkUXTyZqQTgWqjGf3ZMIMWM' // Scheduled flow folder
          : '1a_EbBqivsMyVkESkOLsqqxlgAedxrtEg'; // Voting flow folder (existing)

      // Call the regenerate gram endpoint
      await regenerateGram({
        user_id: targetUserId,
        event_id: eventId,
        google_drive_folder_id: driveFolderId,
        file_format: 'PNG',
      });

      showSnackbar({
        text: 'Gram regeneration started successfully',
        type: 'success',
      });
    } catch (error) {
      // Clear cooldown on error
      setRegenerateGramCooldownEnd(null);
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }

      if (error instanceof AuthFlowError) return;

      console.error('Error regenerating gram:', error);
      showSnackbar({
        text: 'Failed to regenerate gram',
        type: 'error',
      });
    }
  };

  // Only show share button for sessions where sharing is enabled
  // Show for Voting, Scheduled, and Confirmed statuses under the same conditions
  if (
    !canShare ||
    (sessionStatus !== SessionStatus.Voting &&
      sessionStatus !== SessionStatus.Scheduled &&
      sessionStatus !== SessionStatus.Confirmed)
  ) {
    return null;
  }

  return (
    <>
      <Menu
        visible={shouldShowMenu}
        onDismiss={() => {
          setIsMenuVisible(false);
          setIsCreatingShareLink(false);
        }}
        anchor={
          <Tooltip title="Share">
            <View style={[styles.shareButtonContainer, style]}>
              <LinearGradient
                colors={[Colors.purple1, Colors.pink0, Colors.orange0]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.gradientButton}
              >
                <Pressable
                  onPress={handleOpenMenu}
                  style={styles.shareButtonPressable}
                >
                  <HugeiconsIcon
                    icon={Share03Icon}
                    size={16}
                    color={Colors.tint}
                  />
                </Pressable>
                {/* Animated shine overlay */}
                <Animated.View
                  style={[
                    styles.shineOverlay,
                    {
                      transform: [{ translateX: shineAnimation }],
                    },
                  ]}
                >
                  <LinearGradient
                    colors={[
                      'transparent',
                      'rgba(255, 255, 255, 0.6)',
                      'transparent',
                    ]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={styles.shineGradient}
                  />
                </Animated.View>
              </LinearGradient>
            </View>
          </Tooltip>
        }
        contentStyle={menuContentStyle}
      >
        <Menu.Item
          onPress={handleShareToLinkedIn}
          title="Share to LinkedIn"
          titleStyle={{
            color: existingSocialShare ? Colors.text : Colors.opacity50,
          }}
          disabled={!existingSocialShare}
        />
        <Menu.Item
          onPress={handleCopyLink}
          title={existingSocialShare ? 'Copy Link' : 'Generating Link...'}
          titleStyle={{
            color: existingSocialShare ? Colors.text : Colors.opacity50,
          }}
          disabled={!existingSocialShare}
        />
        <Menu.Item
          onPress={handleDownloadGram}
          title="Download Gram"
          titleStyle={{ color: Colors.text }}
        />
        <Menu.Item
          onPress={handleRegenerateGram}
          title="Regenerate Gram"
          titleStyle={{ color: Colors.text }}
        />
      </Menu>

      <Modal
        visible={showCopyLinkModal}
        hide={() => setShowCopyLinkModal(false)}
        modalStyle={styles.modal}
        contentStyle={styles.modalContent}
      >
        <View style={styles.modalHeader}>
          <TextReg style={styles.modalTitle}>Copy Share Link</TextReg>
          <TextReg style={styles.modalSubtitle}>
            Here is your unique share link!
          </TextReg>
        </View>

        <View style={styles.linkContainer}>
          <TextReg style={styles.shareableLink} selectable={true}>
            {shareableLinkForModal}
          </TextReg>
        </View>

        <View style={styles.modalActions}>
          <Button onPress={() => setShowCopyLinkModal(false)}>Close</Button>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  shareButtonContainer: {
    borderRadius: 20,
  },
  gradientButton: {
    borderRadius: 20,
    padding: 2,
    overflow: 'hidden',
  },
  shareButtonPressable: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  shineOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 20,
    overflow: 'hidden',
  },
  shineGradient: {
    width: 40,
    height: '100%',
    transform: [{ skewX: '-15deg' }],
  },
  modal: {
    maxWidth: 500,
    width: '90%',
    alignSelf: 'center',
  },
  modalContent: {
    padding: 24,
  },
  modalHeader: {
    marginBottom: 20,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
    color: Colors.text,
    textAlign: 'center',
  },
  modalSubtitle: {
    fontSize: 14,
    color: Colors.text,
    opacity: 0.8,
    textAlign: 'center',
  },
  linkContainer: {
    backgroundColor: Colors.purple0,
    padding: 12,
    borderRadius: 30,
    borderWidth: 1,
    borderColor: Colors.purple1,
    marginBottom: 20,
  },
  shareableLink: {
    fontSize: 14,
    color: Colors.text,
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier',
    lineHeight: 20,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
});
